/**
 * Modal class to handle modal windows.
 *
 * Usage:
 * 1) Init modal
 *    const modal = new Modal('example');
 *    modal.open();  // open manually
 *    modal.close(); // close manually
 *
 * Elements with [data-modal-target] will open modal
 */
export default class Modal {
    constructor(modalId) {
        this.onOpenCallback = null;
        this.onCloseCallback = null;
        this.bodyChildren = document.querySelectorAll('body > *');
        this.modalId = modalId;
        // Updated: Look for dialog element with data-modal attribute
        this.dialog = document.querySelector(`dialog[data-modal="${modalId}"]`);
        if (!this.dialog)
            return;
        // For backward compatibility, keep overlay if it exists
        this.overlay = this.dialog.querySelector('.modal-window-overlay');
        this.modalContainer = this.dialog.querySelector('.modal-window-container');
        this.closeButton = this.dialog.querySelector('[data-modal-close]');
        this.closeTriggers = this.dialog.querySelectorAll('[data-modal-close]');
        this.closeTriggersDefaultEnabled = this.dialog.querySelectorAll('[data-modal-close-defaultenabled]');
        this.triggers = document.querySelectorAll(`[data-modal-target="${modalId}"]`);
        this.focusableElements = this.dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (this.focusableElements.length > 0) {
            this.firstFocusableElement = this.focusableElements[0];
            this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
        }
        // Initialize aria-hidden to true since modal is closed by default
        this.dialog.setAttribute('aria-hidden', 'true');
        // make all elements non-focusable
        this.makeModalElementsNonFocusable();
        this.init();
    }
    makeModalElementsNonFocusable() {
        const allInteractiveElements = this.dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex], iframe, object, embed, details, summary, video, audio');
        allInteractiveElements.forEach((el) => {
            const element = el;
            // store original tabindex
            if (!element.hasAttribute('data-original-tabindex')) {
                const currentTabindex = element.getAttribute('tabindex');
                element.setAttribute('data-original-tabindex', currentTabindex || '');
            }
            // set tabindex to -1
            element.setAttribute('tabindex', '-1');
            // set inert to prevent interaction
            element.setAttribute('inert', 'true');
        });
    }
    makeModalElementsFocusable() {
        const elementsWithOriginalTabindex = this.dialog.querySelectorAll('[data-original-tabindex]');
        elementsWithOriginalTabindex.forEach((el) => {
            const element = el;
            const originalTabindex = element.getAttribute('data-original-tabindex');
            // restore original tabindex
            if (originalTabindex === '') {
                element.removeAttribute('tabindex');
            }
            else {
                element.setAttribute('tabindex', originalTabindex);
            }
            // remove inert attribute
            element.removeAttribute('inert');
        });
    }
    init() {
        var _a, _b;
        // set initial aria attributes
        this.triggers.forEach(trigger => {
            var _a;
            trigger.setAttribute('aria-haspopup', 'dialog');
            trigger.setAttribute('aria-expanded', 'false');
            if (!trigger.hasAttribute('aria-label') && !((_a = trigger.textContent) === null || _a === void 0 ? void 0 : _a.trim())) {
                trigger.setAttribute('aria-label', `Open ${this.modalId} modal`);
            }
        });
        // add click event to all triggers
        this.triggers.forEach(trigger => {
            trigger.addEventListener('click', (event) => {
                event.preventDefault();
                this.open();
            });
            // add keyboard listeners
            trigger.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    this.open();
                }
            });
        });
        // add click event to all close buttons
        (_a = this.closeButton) === null || _a === void 0 ? void 0 : _a.addEventListener('click', (event) => {
            this.close();
        });
        this.closeTriggers.forEach(closeTrigger => {
            closeTrigger.addEventListener('click', (event) => {
                event.preventDefault();
                this.close();
            });
        });
        // add click event to close buttons with default enabled
        this.closeTriggersDefaultEnabled.forEach(closeTrigger => {
            closeTrigger.addEventListener('click', (event) => {
                this.close();
            });
        });
        // close when clicking on overlay (if overlay exists)
        (_b = this.overlay) === null || _b === void 0 ? void 0 : _b.addEventListener('click', (event) => {
            this.close();
        });
        // Native dialog escape key handling
        this.dialog.addEventListener('cancel', (event) => {
            event.preventDefault();
            this.close();
        });
        // handle keyboard navigation for custom tab trapping (optional, native handles it)
        this.dialog.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                // Native dialog handles escape, but we'll still call close
                this.close();
            }
            if (event.key === 'Tab') {
                this.handleTabKey(event);
            }
        });
    }
    handleTabKey(event) {
        // only handle tab if dialog is open
        if (!this.dialog.open)
            return;
        if (this.focusableElements.length === 0)
            return;
        if (event.shiftKey) {
            if (document.activeElement === this.firstFocusableElement) {
                event.preventDefault();
                this.lastFocusableElement.focus();
            }
        }
        else {
            if (document.activeElement === this.lastFocusableElement) {
                event.preventDefault();
                this.firstFocusableElement.focus();
            }
        }
    }
    open() {
        // previously focused element
        this.previouslyFocusedElement = document.activeElement;
        // Remove any autofocus attributes inside the dialog to prevent
        // VoiceOver from reading the title multiple times. The dialog's
        // aria-labelledby already announces the title once on open.
        this.dialog.querySelectorAll('[autofocus]').forEach(element => {
            element.removeAttribute('autofocus');
        });
        // showModal() with no [autofocus] element focuses the <dialog> itself.
        // Combined with aria-labelledby, VoiceOver announces the title exactly once.
        this.dialog.showModal();
        // Make all modal elements focusable after dialog is open
        this.makeModalElementsFocusable();
        // Update CSS class and aria-hidden for styling
        this.dialog.classList.add('active');
        this.dialog.setAttribute('aria-hidden', 'false');
        document.body.classList.add('no-scroll');
        // update trigger aria-expanded
        this.triggers.forEach(trigger => {
            trigger.setAttribute('aria-expanded', 'true');
        });
        // re-fetch focusable elements
        this.focusableElements = this.dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (this.focusableElements.length > 0) {
            this.firstFocusableElement = this.focusableElements[0];
            this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
        }
        this.toggleBackgroundInert(true);
        // call onOpen callback
        if (this.onOpenCallback) {
            this.onOpenCallback();
        }
    }
    close() {
        // make modal elements non-focusable
        this.makeModalElementsNonFocusable();
        // Use native dialog method
        this.dialog.close();
        // Update CSS class and aria-hidden for styling
        this.dialog.classList.remove('active');
        this.dialog.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('no-scroll');
        this.toggleBackgroundInert(false);
        // update trigger aria-expanded
        this.triggers.forEach(trigger => {
            trigger.setAttribute('aria-expanded', 'false');
        });
        // return focus to previously focused element
        if (this.previouslyFocusedElement) {
            this.previouslyFocusedElement.focus();
        }
        // call onClose callback
        if (this.onCloseCallback) {
            this.onCloseCallback();
        }
    }
    // add or remove inert and aria-hidden attributes
    toggleBackgroundInert(inert) {
        this.bodyChildren.forEach((el) => {
            if (el !== this.dialog && !el.contains(this.dialog)) {
                if (inert) {
                    el.setAttribute('inert', 'true');
                    el.setAttribute('aria-hidden', 'true');
                }
                else {
                    el.removeAttribute('inert');
                    el.removeAttribute('aria-hidden');
                }
            }
        });
    }
    onOpen(callback) {
        this.onOpenCallback = callback;
    }
    onClose(callback) {
        this.onCloseCallback = callback;
    }
}
